import { useMutation } from 'react-query'
import { postFileAPI } from 'framework/api/http'

interface GetOrdersExportCsvInterface {
  status?: any
  pId?: any
  starDate?: string
  endDate?: string
  isInternational?: boolean
  searchKeyword?: string
  selectedOrderType?: string
  partnerCodes?: any
  partnerOrderTypes?: any
}

/** Empty array / empty string means "no restriction" — send null so the backend skips the filter. */
const emptyToNull = (value: any) => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  if (Array.isArray(value) && value.length === 0) {
    return null
  }
  return value
}

export const useGetOrdersExportCSV = () => {
  const mutate = useMutation(
    ({
      status,
      pId,
      starDate,
      endDate,
      isInternational,
      searchKeyword,
      selectedOrderType,
      partnerCodes,
      partnerOrderTypes,
    }: GetOrdersExportCsvInterface) => {
      /**
       * BDL20-3126: encode both bounds. They used to be interpolated raw, so a JS Date reached the query
       * string as "Sat Feb 01 2026 00:00:00 GMT+0530 (India Standard Time)". The "+" decodes server-side
       * to a space, DateTime binding fails, and — because the endpoint had From/To defaults and the API
       * project has no [ApiController] — the export silently ran over the wrong window. Callers must pass
       * the same pre-formatted bound strings the order-list grid sends.
       */
      const from = encodeURIComponent(String(starDate ?? ''))
      const to = encodeURIComponent(String(endDate ?? ''))

      /**
       * BDL20-2286: the backend STREAMS text/csv directly (Content-Disposition attachment), so request it
       * as a blob. The returned promise resolves only once the whole stream has been received, which the
       * export toast lifecycle keys off.
       */
      return postFileAPI(`/PartnerOrders/CsvList?From=${from}&To=${to}`, {
        SearchKeyword: searchKeyword === '' ? null : searchKeyword,
        Status: !status?.length || status[0] === 'ALL' ? null : status,
        PartnerIds: emptyToNull(pId),
        isInternational,
        OrderType: selectedOrderType,
        /**
         * BDL20-3126: the two dimensions the Dashboard tiles filter the order list by. Without them the
         * export ignored the tile's partner / order-type filter and returned rows the grid never showed.
         */
        PartnerCodes: emptyToNull(partnerCodes),
        PartnerOrderTypes: emptyToNull(partnerOrderTypes),
      })
    },
  )

  return mutate
}

export default GetOrdersExportCsvInterface
